import { Injectable } from '@angular/core';
import { UtilityFlightService } from './utility-flight.service';

@Injectable({
  providedIn: 'root'
})
export class HubSpokeService {

  // State variables/signals to expose
  isEligible: boolean = false;
  isOutboundEligible: boolean = false;
  isInboundEligible: boolean = false;
  outboundCategory: string = '';
  inboundCategory: string = '';
  outboundLayoverHours: number = 0;
  inboundLayoverHours: number = 0;
  layoverHours: number = 0;
  transitCategory: string = '';
  matchedCarrier: string = '';
  matchedFlightNumber: string = '';
  matchedSector: string = '';
  matchedDate: string = '';
  passengers = { adults: 0, children: 0, infants: 0 };

  readonly isHubOperation = [
    {
      airline_name: 'Air India',
      airline_code: 'AI',
      flight_series: '11',
      hub_operation: [
        { connecting_airport: 'DEL', origin: 'VNS', destination: 'LHR', till_date: '', flight_number: 2017 },
        { connecting_airport: 'DEL', origin: 'VNS', destination: 'ZRH', till_date: '', flight_number: 151 },
        { connecting_airport: 'DEL', origin: 'VNS', destination: 'MNL', till_date: '', flight_number: 2362 },
        { connecting_airport: 'DEL', origin: 'VNS', destination: 'FCO', till_date: '', flight_number: 123 },
        { connecting_airport: 'DEL', origin: 'VNS', destination: 'CHP', till_date: '', flight_number: 157 },
        { connecting_airport: 'DEL', origin: 'VNS', destination: 'FRA', till_date: '', flight_number: 2029 },
        { connecting_airport: 'DEL', origin: 'VNS', destination: 'VIE', till_date: '', flight_number: 153 },
        { connecting_airport: 'DEL', origin: 'VNS', destination: 'MXP', till_date: '', flight_number: 137 },
        { connecting_airport: 'DEL', origin: 'VNS', destination: 'CMB', till_date: '', flight_number: 281 },
        { connecting_airport: 'DEL', origin: 'VNS', destination: 'SGN', till_date: '', flight_number: 2388 },
        { connecting_airport: 'DEL', origin: 'VNS', destination: 'LHR', till_date: '', flight_number: 2015 },
        { connecting_airport: 'DEL', origin: 'VNS', destination: 'SIN', till_date: '', flight_number: 2382 },
        { connecting_airport: 'DEL', origin: 'VNS', destination: 'HKT', till_date: '', flight_number: 2376 },
        { connecting_airport: 'DEL', origin: 'VNS', destination: 'RUL', till_date: '', flight_number: 2243 },
        { connecting_airport: 'DEL', origin: 'VNS', destination: 'KUL', till_date: '', flight_number: 2384 },
        { connecting_airport: 'DEL', origin: 'VNS', destination: 'BKK', till_date: '', flight_number: 2304 },
        { connecting_airport: 'DEL', origin: 'VNS', destination: 'KTM', till_date: '', flight_number: 219 },
        { connecting_airport: 'DEL', origin: 'VNS', destination: 'DXB', till_date: '2026-07-31', flight_number: 4309 },
      ]
    }
  ];

  constructor(private utilityFlightService: UtilityFlightService) { }

  /**
   * Helper to parse date and time strings robustly.
   */
  private parseDateTime(dateStr: string, timeStr: string): Date {
    let normalizedDate = (dateStr || '').trim();
    if (normalizedDate.includes('-')) {
      const parts = normalizedDate.split('-');
      if (parts[0].length === 2) {
        // DD-MM-YYYY -> YYYY-MM-DD
        normalizedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    } else if (normalizedDate.includes('/')) {
      const parts = normalizedDate.split('/');
      if (parts[0].length === 2) {
        // DD/MM/YYYY -> YYYY-MM-DD
        normalizedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      } else if (parts[2].length === 2) {
        // MM/DD/YY -> 20YY-MM-DD
        normalizedDate = `20${parts[2]}-${parts[0]}-${parts[1]}`;
      }
    }
    const time = (timeStr || '00:00').trim();
    return new Date(`${normalizedDate}T${time}:00`);
  }

  /**
   * Extracts passenger counts from the current URL path or falls back to localStorage.
   */
  getPassengersFromUrl(): { adults: number; children: number; infants: number } {
    try {
      const url = window.location.href;
      // Look for "/[adults],[children],[infants];" pattern in fragment or URL
      const match = url.match(/\/(\d+),(\d+),(\d+)(;|%3B)/);
      if (match) {
        return {
          adults: parseInt(match[1], 10),
          children: parseInt(match[2], 10),
          infants: parseInt(match[3], 10)
        };
      }
    } catch (e) {
      // Fail-silent fallback
    }

    // Fallback to localStorage keys
    return {
      adults: parseInt(localStorage.getItem('adultValue') || '0', 10),
      children: parseInt(localStorage.getItem('childValue') || '0', 10),
      infants: parseInt(localStorage.getItem('infantsValue') || '0', 10)
    };
  }

  /**
   * Checks if the flight journey is eligible for the Hub & Spoke programme.
   */
  isHubSpokeEligible(flight: any): boolean {
    // Reset state
    this.isEligible = false;
    this.isOutboundEligible = false;
    this.isInboundEligible = false;
    this.outboundCategory = '';
    this.inboundCategory = '';
    this.outboundLayoverHours = 0;
    this.inboundLayoverHours = 0;
    this.layoverHours = 0;
    this.transitCategory = '';
    this.matchedCarrier = '';
    this.matchedFlightNumber = '';
    this.matchedSector = '';
    this.matchedDate = '';
    this.passengers = { adults: 0, children: 0, infants: 0 };

    if (!flight || !flight.flightlegs) {
      return false;
    }

    const legsArray = flight.flightlegs;
    if (!Array.isArray(legsArray) || legsArray.length === 0) {
      return false;
    }

    const bounds = Array.isArray(legsArray[0]) ? legsArray : [legsArray];

    for (let boundIndex = 0; boundIndex < bounds.length; boundIndex++) {
      const boundLegs = bounds[boundIndex];
      if (!Array.isArray(boundLegs) || boundLegs.length === 0) {
        continue;
      }

      // Check outbound eligibility first
      const outboundRes = this.checkOutboundEligibility(boundLegs, flight, boundIndex);
      if (outboundRes.eligible) {
        this.isOutboundEligible = true;
        this.outboundCategory = outboundRes.category;
        this.outboundLayoverHours = outboundRes.layoverHours;
        this.isEligible = true;
      } else {
        // Check inbound eligibility
        const inboundRes = this.checkInboundEligibility(boundLegs, flight, boundIndex);
        if (inboundRes.eligible) {
          this.isInboundEligible = true;
          this.inboundCategory = inboundRes.category;
          this.inboundLayoverHours = inboundRes.layoverHours;
          this.isEligible = true;
        }
      }
    }

    if (this.isEligible) {
      // Legacy compatibility properties
      if (this.isOutboundEligible) {
        this.transitCategory = this.outboundCategory;
        this.layoverHours = this.outboundLayoverHours;
      } else if (this.isInboundEligible) {
        this.transitCategory = this.inboundCategory;
        this.layoverHours = this.inboundLayoverHours;
      }

      this.passengers = this.getPassengersFromUrl();
      const firstLeg = bounds[0]?.[0];
      if (firstLeg) {
        this.matchedCarrier = firstLeg.carrier || '';
        this.matchedFlightNumber = firstLeg.flightnumber || '';
        this.matchedSector = `${firstLeg.origin}-${firstLeg.destination}`;
        this.matchedDate = firstLeg.depdate || '';
      }
      return true;
    }

    return false;
  }

  checkOutboundEligibility(boundLegs: any[], flight: any, boundIndex: number): { eligible: boolean, category: string, layoverHours: number } {
    if (!Array.isArray(boundLegs) || boundLegs.length === 0) {
      return { eligible: false, category: '', layoverHours: 0 };
    }

    // DOMESTIC Category (Single leg from origin connecting to DEL)
    if (boundLegs.length === 1) {
      const leg = boundLegs[0];
      const carrier = (leg.carrier || '').toUpperCase().trim();
      const flightnumber = (leg.flightnumber || '').toString().trim();
      
      const isAirIndiaSeries = carrier === 'AI' && /^11\d+$/.test(flightnumber);
      const isTransitAtDelhi = (leg.destination || '').toUpperCase().trim() === 'DEL';

      if (isAirIndiaSeries && isTransitAtDelhi) {
        const origin = (leg.origin || '').toUpperCase().trim();
        const hubOps = this.isHubOperation[0]?.hub_operation || [];
        const matchedOp = hubOps.find(op => 
          op.connecting_airport === 'DEL' && 
          op.origin === origin
        );

        if (matchedOp) {
          let isDateValid = true;
          if (matchedOp.till_date) {
            const flightDate = this.parseDateTime(leg.depdate, '00:00');
            const limitDate = this.parseDateTime(matchedOp.till_date, '00:00');
            isDateValid = flightDate <= limitDate;
          }

          if (isDateValid) {
            return { eligible: true, category: 'DOMESTIC', layoverHours: 0 };
          }
        }
      }
    }

    // INTERNATIONAL or Connecting Domestic Categories (Connecting flights transiting through DEL)
    if (boundLegs.length >= 2) {
      for (let i = 0; i < boundLegs.length - 1; i++) {
        const currentLeg = boundLegs[i];
        const nextLeg = boundLegs[i + 1];

        if (currentLeg && nextLeg) {
          const carrier = (currentLeg.carrier || '').toUpperCase().trim();
          const flightnumber = (currentLeg.flightnumber || '').toString().trim();
          
          const isAirIndiaSeries = carrier === 'AI' && /^11\d+$/.test(flightnumber);

          const isTransitAtDelhi = 
            (currentLeg.destination || '').toUpperCase().trim() === 'DEL' && 
            (nextLeg.origin || '').toUpperCase().trim() === 'DEL';

          if (isAirIndiaSeries && isTransitAtDelhi) {
            const origin = (currentLeg.origin || '').toUpperCase().trim();
            const destination = (nextLeg.destination || '').toUpperCase().trim();
            
            const hubOps = this.isHubOperation[0]?.hub_operation || [];
            const matchedOp = hubOps.find(op => 
              op.connecting_airport === 'DEL' && 
              op.origin === origin && 
              op.destination === destination
            );

            if (matchedOp) {
              let isDateValid = true;
              if (matchedOp.till_date) {
                const flightDate = this.parseDateTime(currentLeg.depdate, '00:00');
                const limitDate = this.parseDateTime(matchedOp.till_date, '00:00');
                isDateValid = flightDate <= limitDate;
              }

              if (isDateValid) {
                let layoverHours = 0;
                const boundLayovers = flight?.layovers?.[boundIndex];
                if (boundLayovers && typeof boundLayovers === 'object') {
                  const delKey = Object.keys(boundLayovers).find(k => k.toUpperCase() === 'DEL');
                  if (delKey && typeof boundLayovers[delKey] === 'number') {
                    layoverHours = boundLayovers[delKey] / 3600;
                  }
                }

                if (layoverHours === 0) {
                  const arrival = this.parseDateTime(currentLeg.arrdate, currentLeg.arrtime);
                  const departure = this.parseDateTime(nextLeg.depdate, nextLeg.deptime);
                  const layoverMs = departure.getTime() - arrival.getTime();
                  layoverHours = layoverMs / (1000 * 60 * 60);
                }

                const category = layoverHours <= 4 ? 'INTERNATIONAL (Within 4 Hours)' : 'INTERNATIONAL (Beyond 4 Hours)';
                return { eligible: true, category, layoverHours: parseFloat(layoverHours.toFixed(2)) };
              }
            } else {
              // Connecting flight but not to an eligible international destination in hubOps (e.g. domestic connection)
              const isFeederOrigin = hubOps.some(op => op.origin === origin);
              if (isFeederOrigin) {
                return { eligible: true, category: 'DOMESTIC', layoverHours: 0 };
              }
            }
          }
        }
      }
    }

    return { eligible: false, category: '', layoverHours: 0 };
  }

  checkInboundEligibility(boundLegs: any[], flight: any, boundIndex: number): { eligible: boolean, category: string, layoverHours: number } {
    if (!Array.isArray(boundLegs) || boundLegs.length < 2) {
      return { eligible: false, category: '', layoverHours: 0 };
    }

    // Inbound: International to DEL to Domestic
    // Second leg (domestic) starts with AI-11XX
    for (let i = 0; i < boundLegs.length - 1; i++) {
      const currentLeg = boundLegs[i];
      const nextLeg = boundLegs[i + 1];

      if (currentLeg && nextLeg) {
        const nextCarrier = (nextLeg.carrier || '').toUpperCase().trim();
        const nextFlightnumber = (nextLeg.flightnumber || '').toString().trim();
        
        const isNextAirIndiaSeries = nextCarrier === 'AI' && /^11\d+$/.test(nextFlightnumber);

        const isTransitAtDelhi = 
          (currentLeg.destination || '').toUpperCase().trim() === 'DEL' && 
          (nextLeg.origin || '').toUpperCase().trim() === 'DEL';

        if (isNextAirIndiaSeries && isTransitAtDelhi) {
          const origin = (currentLeg.origin || '').toUpperCase().trim(); // International origin (e.g. ZRH)
          const destination = (nextLeg.destination || '').toUpperCase().trim(); // Domestic destination (e.g. VNS)
          
          const hubOps = this.isHubOperation[0]?.hub_operation || [];
          // Search in hubOps for connecting DEL and matching outbound pairs (origin: VNS, destination: ZRH)
          // Since it's inbound, we swap them: op.origin is VNS (destination) and op.destination is ZRH (origin)
          const matchedOp = hubOps.find(op => 
            op.connecting_airport === 'DEL' && 
            op.origin === destination && 
            op.destination === origin
          );

          if (matchedOp) {
            let isDateValid = true;
            if (matchedOp.till_date) {
              const flightDate = this.parseDateTime(currentLeg.depdate, '00:00');
              const limitDate = this.parseDateTime(matchedOp.till_date, '00:00');
              isDateValid = flightDate <= limitDate;
            }

            if (isDateValid) {
              let layoverHours = 0;
              const boundLayovers = flight?.layovers?.[boundIndex];
              if (boundLayovers && typeof boundLayovers === 'object') {
                const delKey = Object.keys(boundLayovers).find(k => k.toUpperCase() === 'DEL');
                if (delKey && typeof boundLayovers[delKey] === 'number') {
                  layoverHours = boundLayovers[delKey] / 3600;
                }
              }

              if (layoverHours === 0) {
                const arrival = this.parseDateTime(currentLeg.arrdate, currentLeg.arrtime);
                const departure = this.parseDateTime(nextLeg.depdate, nextLeg.deptime);
                const layoverMs = departure.getTime() - arrival.getTime();
                layoverHours = layoverMs / (1000 * 60 * 60);
              }

              // Condition: MCT: within 4 hrs
              if (layoverHours <= 4) {
                return { eligible: true, category: 'INBOUND (Within 4 Hours)', layoverHours: parseFloat(layoverHours.toFixed(2)) };
              }
            }
          } else {
            // Connecting flight but not from an eligible international origin in hubOps (e.g. domestic connection)
            const isFeederDest = hubOps.some(op => op.origin === destination);
            if (isFeederDest) {
              return { eligible: true, category: 'DOMESTIC', layoverHours: 0 };
            }
          }
        }
      }
    }

    return { eligible: false, category: '', layoverHours: 0 };
  }
}
